import { Plus, X, GripVertical } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

interface StringListEditorProps {
  idPrefix: string;
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  addLabel: string;
  deleteLabel: string;
  testIdPrefix: string;
  disabled?: boolean;
}

/** Generic add/edit/remove list of plain strings — backs field `options` (Select/MultiSelect) and Grid rows/columns. */
export function StringListEditor({
  idPrefix,
  label,
  values,
  onChange,
  addLabel,
  deleteLabel,
  testIdPrefix,
  disabled = false,
}: StringListEditorProps) {
  function updateValue(index: number, value: string) {
    const next = [...values];
    next[index] = value;
    onChange(next);
  }

  function removeValue(index: number) {
    onChange(values.filter((_, i) => i !== index));
  }

  function addValue() {
    onChange([...values, '']);
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {values.map((value, index) => (
        <div key={`${idPrefix}-${testIdPrefix}-${index}`} className="flex items-center gap-2">
          <GripVertical className="h-3 w-3 text-muted-foreground shrink-0" aria-hidden="true" />
          <Input
            value={value}
            onChange={(e) => updateValue(index, e.target.value)}
            disabled={disabled}
            data-testid={`${testIdPrefix}-input-${index}`}
            className="h-8 text-sm"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => removeValue(index)}
                disabled={disabled}
                aria-label={deleteLabel}
                data-testid={`${testIdPrefix}-delete-${index}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{deleteLabel}</TooltipContent>
          </Tooltip>
        </div>
      ))}
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addValue}
          data-testid={`${testIdPrefix}-add-button`}
        >
          <Plus className="h-3 w-3 mr-1" />
          {addLabel}
        </Button>
      )}
    </div>
  );
}
